import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import { Check, Dot, Trash2, TriangleAlert } from "lucide-react";
import React, { useEffect } from "react";
import styled from "styled-components";
import { SelectFormInput, TextInput } from "../../components/inputs";
import SubmitButton from "../../components/SubmitButton";
import { StyledTableContainer } from "../../components/Tables";
import { useApi } from "../../contexts/ApiContext";
import { useApp } from "../../contexts/AppContext";
import {
  ClinicMember,
  ClinicMembersResponse,
  ClinicMemberStatus,
  InviteFormData,
  InviteFormResponse,
  inviteSchema,
} from "./types";
import { useError } from "../../contexts/ErrorContext";
import Progress from "../../components/Progress";
import { colors, fontSizes } from "../../theme";
import { Link } from "react-router-dom";

enum Role {
  Admin = 1,
  Member = 2,
}

interface MemberInvirationRecord extends Omit<ClinicMember, "role"> {
  role: Role | null;
  invitationSent?: boolean;
}

const StyledTableContainerNoBoders = styled(StyledTableContainer)({
  border: "none",
  borderRadius: "8px",
  backgroundColor: "white",
  "& .MuiTableCell-root": {
    borderBottom: "none",
    fontSize: 14,
    padding: "8px 4px",
    verticalAlign: "top",
  },
});

const EmptyState: React.FC<{ onInvite: () => void }> = ({ onInvite }) => {
  const { translate } = useApp();
  const translations = translate("manage");
  return (
    <Stack
      spacing={2}
      alignSelf={"center"}
      width={"50%"}
      maxWidth={600}
      sx={{ margin: "0 auto" }}
      alignItems={"center"}
    >
      <Typography variant={"caption"} fontSize={18}>
        {translations.sections.members.empty_state.description}
      </Typography>
      <Box width={160}>
        <SubmitButton onClick={onInvite}>
          {translations.sections.members.buttons.invite}
        </SubmitButton>
      </Box>
    </Stack>
  );
};

interface InviteDialogProps {
  open: boolean;
  onClose: () => void;
  onSend: (members: MemberInvirationRecord[]) => void;
  errors?: { [key: string]: string };
  successfulInvites?: { [key: string]: boolean };
}

const emptyMember: MemberInvirationRecord = {
  email: "",
  role: null,
  name: "",
  id: "",
  status: ClinicMemberStatus.Active,
};

const InviteDialog: React.FC<InviteDialogProps> = ({
  open,
  onClose,
  onSend,
  errors: invitationErrors = {},
  successfulInvites = {},
}) => {
  const { translate } = useApp();
  const translations = translate("manage");
  const [members, setMembers] = React.useState<MemberInvirationRecord[]>([
    emptyMember,
  ]);
  const [errors, setErrors] = React.useState<{ [key: string]: string }[]>([]);

  const handleNameChange = (index: number, value: string) => {
    const newMembers = [...members];
    newMembers[index].name = value;
    setMembers(newMembers);
  };

  useEffect(() => {
    setMembers((prev) => prev.filter((m) => !successfulInvites[m.name]));
  }, [successfulInvites]);

  const handleEmailChange = (index: number, value: string) => {
    const newMembers = [...members];
    newMembers[index].email = value;
    if (!newMembers[index].name && value.includes("@")) {
      newMembers[index].name = value.split("@")[0];
    }
    setMembers(newMembers);
  };

  const handleRoleChange = (index: number, value: string) => {
    const newMembers = [...members];
    newMembers[index].role = value === "1" ? Role.Admin : Role.Member;
    setMembers(newMembers);
  };

  const validate = () => {
    const schema = inviteSchema;
    const tErrors: { [key: string]: string }[] = [];
    const errCount = members.filter((member, index) => {
      const result = schema.safeParse({
        ...member,
        ...{ username: member.name },
      });
      console.log(`send validate`, result);

      const errObj: any = {};
      if (!result.success) {
        result.error.errors.forEach((error) => {
          const field = error.path[0];
          const message = error.message;
          const translatedMessage = translate("manage", message);
          errObj[field] = translatedMessage;
        });
        tErrors.push(errObj);
        return true;
      }
      return false;
    });

    console.log(`errors`, tErrors);
    setErrors(tErrors);

    return errCount.length === 0;
  };

  const handleInvite = () => {
    console.log(`send invite`);
    if (!validate()) {
      return;
    }
    onSend(members);
  };

  const handleDelete = (index: number) => {
    const newMembers = [...members];
    newMembers.splice(index, 1);
    setMembers(newMembers);

    const newErrors = [...errors];
    newErrors.splice(index, 1);
    setErrors(newErrors);
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        <Typography variant={"medium"}>
          {translations.sections.members.dialogs.invite.title}
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={1}>
          <StyledTableContainerNoBoders>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell></TableCell>
                  <TableCell>
                    {
                      translations.sections.members.dialogs.invite.fields
                        .username
                    }
                  </TableCell>
                  <TableCell>
                    {translations.sections.members.dialogs.invite.fields.email}
                  </TableCell>
                  <TableCell>
                    {translations.sections.members.dialogs.invite.fields.role}
                  </TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {members.map((member, index) => (
                  <TableRow key={index}>
                    <TableCell width={"5%"} align={"center"}>
                      {invitationErrors[member.name] && (
                        <Tooltip title={invitationErrors[member.name]}>
                          <Box position={"relative"} top={8}>
                          <TriangleAlert color={colors.red[500]} />
                          </Box>
                        </Tooltip>
                      )}
                    </TableCell>
                    <TableCell width={"30%"}>
                      <TextInput
                        error={!!errors[index]?.name}
                        helperText={errors[index]?.name ?? " "}
                        fullWidth
                        label=""
                        value={member.name ?? ""}
                        onChange={(value) =>
                          handleNameChange(index, value as string)
                        }
                      />
                    </TableCell>
                    <TableCell width={"100%"}>
                      <TextInput
                        error={!!errors[index]?.email}
                        helperText={errors[index]?.email}
                        fullWidth
                        label=""
                        value={member.email}
                        onChange={(value) => {
                          handleEmailChange(index, value as string);
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <SelectFormInput
                        error={!!errors[index]?.role}
                        helperText={errors[index]?.role}
                        sx={{ minWidth: 120 }}
                        label=""
                        value={member.role}
                        onChange={(value) => {
                          handleRoleChange(index, value as string);
                        }}
                        options={[
                          { label: "Admin", value: "1" },
                          { label: "Member", value: "2" },
                        ]}
                      />
                    </TableCell>
                    <TableCell>
                      <IconButton
                        disabled={members.length === 1}
                        onClick={() => {
                          handleDelete(index);
                        }}
                      >
                        <Trash2 />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </StyledTableContainerNoBoders>
          <Button
            fullWidth
            color="greyButton"
            onClick={() => setMembers([...members, { ...emptyMember }])}
          >
            {translations.sections.members.dialogs.invite.buttons.add}
          </Button>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="whiteButton">
          {translations.sections.members.dialogs.invite.buttons.cancel}
        </Button>
        <Box width={160}>
          <SubmitButton onClick={handleInvite} color="blackButton">
            {translations.sections.members.dialogs.invite.buttons.send}
          </SubmitButton>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

const MembersTable: React.FC<{
  members: ClinicMember[];
  onRemove: (member: ClinicMember) => void;
  onResend: (member: ClinicMember) => void;
}> = ({ members, onRemove, onResend }) => {
  return (
    <StyledTableContainer>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Member</TableCell>
            <TableCell>Email</TableCell>
            <TableCell>Role</TableCell>
            <TableCell>Status</TableCell>
            <TableCell></TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {members.map((member, index) => (
            <TableRow key={index}>
              <TableCell>
                <Typography fontSize={fontSizes.sm}>{member.name}</Typography>
              </TableCell>
              <TableCell>
                <Typography fontSize={fontSizes.sm}>{member.email}</Typography>
              </TableCell>
              <TableCell>
                <Typography fontSize={fontSizes.sm}>
                  {member.role === 1 ? "Admin" : "Member"}
                </Typography>
              </TableCell>
              <TableCell>
                {member.status === ClinicMemberStatus.Active ? (
                  <Typography fontSize={fontSizes.sm}>Active</Typography>
                ) : (
                  <Stack direction={"row"} gap={1} alignItems={"center"}>
                    <Typography fontSize={fontSizes.sm} color={"#4C1D95"}>
                      Pending
                    </Typography>
                    <Dot />
                    <Button variant="text" onClick={() => onResend(member)}>
                      <Typography
                        sx={{ textDecoration: "underline" }}
                        variant="body2"
                        color="#0C0A09"
                      >
                        Resend Invite
                      </Typography>
                    </Button>
                  </Stack>
                )}
              </TableCell>
              <TableCell>
                <IconButton onClick={() => onRemove(member)}>
                  <Trash2 />
                </IconButton>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </StyledTableContainer>
  );
};

const ConfirmationDialog: React.FC<{
  open: boolean;
  username: string;
  onRemove: (username: string) => void;
  onClose: () => void;
}> = ({ open, username, onRemove, onClose }) => {
  const { translate } = useApp();
  const { apiService, setSubmitting } = useApi();
  const { showError, showSuccess } = useError();

  const handleRemove = async () => {
    try {
      setSubmitting(true);
      await apiService.delete<null, { username: string }>(`/clinic/user`, {
        username,
      });
      showSuccess(
        translate(
          "manage",
          "sections.members.dialogs.remove_confirm.messages.success",
          { username }
        )
      );
      onRemove(username);
    } catch (error) {
      showError(
        translate(
          "manage",
          "sections.members.dialogs.remove_confirm.messages.fail",
          { username }
        )
      );
      console.error(`Failed to remove user`, error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>
        {translate("manage", "sections.members.dialogs.remove_confirm.title")}
      </DialogTitle>
      <DialogContent>
        <Typography variant={"medium"}>
          {translate(
            "manage",
            "sections.members.dialogs.remove_confirm.subtitle",
            { username }
          )}
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button color="whiteButton" onClick={onClose}>
          {translate(
            "manage",
            "sections.members.dialogs.remove_confirm.buttons.cancel"
          )}
        </Button>
        <Box width={160}>
          <SubmitButton onClick={handleRemove}>
            {translate(
              "manage",
              "sections.members.dialogs.remove_confirm.buttons.remove"
            )}
          </SubmitButton>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

const Members: React.FC<{ clinicName: string | null }> = ({ clinicName }) => {
  const { translate } = useApp();
  const translations = translate("manage");
  const { apiService, setSubmitting, isLoading, setLoading } = useApi();
  const [members, setMembers] = React.useState<ClinicMember[]>([]);
  const [errors, setErrors] = React.useState<{ [name: string]: string }>({});
  const { showSuccess, showError } = useError();
  const [inviteDialogOpen, setInviteDialogOpen] = React.useState(false);
  const [usernameToRemove, setUsernameToRemove] = React.useState<string | null>(
    null
  );
  const [successfulInvites, setSuccessfulInvites] = React.useState<{
    [key: string]: boolean;
  }>({});

  useEffect(() => {
    fetchMembers();
  }, []);

  const openInviteDialog = () => {
    if (!clinicName) {
      showError(translations.sections.members.errors.name.required);
      return;
    }
    setInviteDialogOpen(true);
  };

  const handleSendInvitations = async (
    newMembers: MemberInvirationRecord[]
  ) => {
    setSubmitting(true);

    const promises = newMembers.map((member) => {
      return new Promise<{
        member: MemberInvirationRecord;
        success: boolean;
        error?: string;
      }>(async (resolve) => {
        try {
          console.info(`member.role as number`, member.role as number);

          const response = await apiService.put<
            InviteFormResponse,
            InviteFormData
          >("/clinic/user", {
            email: member.email,
            role: member.role as number,
            username: member.name!,
          });
          if (response.created) {
            resolve({ member, success: true });
          } else {
            resolve({
              member,
              success: false,
              error: translate(
                "manage",
                "sections.members.errors.invite_fail",
                { username: member.email }
              ),
            });
          }
        } catch (error) {
          resolve({
            member,
            success: false,
            error: translate("manage", "sections.members.errors.invite_fail", {
              username: member.email,
            }),
          });
        }
      });
    });

    const results = await Promise.all(promises);

    const rejectedInvites = results.filter((result) => !result.success);
    const invitationErrors: { [index: string]: string } = {};
    const successful: { [index: string]: boolean } = {};
    results.forEach((result) => {
      successful[result.member.name!] = result.success;
    });
    setSuccessfulInvites(successful);
    rejectedInvites.forEach((result) => {
      invitationErrors[result.member.name!] = result.error || "Unknown error";
    });

    if (rejectedInvites.length) {
      showError(
        translate(
          "manage",
          "sections.members.dialogs.invite.messages.invite_fail",
          {
            count: `${rejectedInvites.length}`,
          }
        )
      );
    } else {
      setInviteDialogOpen(false);
      showSuccess(
        translations.sections.members.dialogs.invite.messages.invite_success
      );
    }

    setErrors(invitationErrors);

    setMembers((prevMembers) => [
      ...prevMembers,
      ...results
        .filter((result) => result.success)
        .map((result) => ({
          ...result.member,
          role: result.member.role as Role,
          invitationSent: result.success,
        })),
    ]);
    setSubmitting(false);

    fetchMembers();
  };

  const fetchMembers = async () => {
    try {
      setLoading(true);
      const response = await apiService.get<ClinicMembersResponse>(
        "/clinic/user"
      );
      setMembers(
        response.users.map((member) => ({
          ...member,
          role: member.role === 1 ? Role.Admin : Role.Member,
        }))
      );
    } catch (error: any) {
      console.error(`fetchMembers error`, error.message);
    } finally {
      setLoading(false);
    }
  };

  const onMemberRemove = (username: string) => {
    const newMembers = members.filter((member) => member.name !== username);
    setMembers(newMembers);
  };

  const handleCloseRemoveDialog = () => {
    setUsernameToRemove(null);
  };

  const handleMemberRemoveClick = (member: ClinicMember) => {
    console.info(`handleMemberRemoveClick`, member);
    setUsernameToRemove(member.name);
  };

  return (
    <Stack spacing={2}>
      <Stack direction={"row"} justifyContent={"space-between"}>
        <Typography variant={"medium"}>
          {translations.sections.members.title}
        </Typography>
        {members.length > 0 && (
          <SubmitButton sx={{ width: 160 }} onClick={openInviteDialog}>
            {translations.sections.members.buttons.invite}
          </SubmitButton>
        )}
      </Stack>
      {isLoading ? (
        <Progress height={120} />
      ) : members.length > 0 ? (
        <MembersTable
          onResend={(member) => handleSendInvitations([member])}
          members={members}
          onRemove={handleMemberRemoveClick}
        />
      ) : (
        <EmptyState onInvite={openInviteDialog} />
      )}
      <InviteDialog
        errors={errors}
        onSend={handleSendInvitations}
        open={inviteDialogOpen}
        onClose={() => setInviteDialogOpen(false)}
        successfulInvites={successfulInvites}
      />
      <ConfirmationDialog
        open={!!usernameToRemove}
        username={usernameToRemove ?? ""}
        onRemove={onMemberRemove}
        onClose={handleCloseRemoveDialog}
      />
    </Stack>
  );
};

export default Members;
